import { useTranslation } from '@doccareplus/shared/lib';
import { toISODateString } from '@doccareplus/shared/lib/date';
import { nationalPhoneLengthLimit } from '@doccareplus/shared/lib/phone';
import { DateField, Input, PhoneInput, SearchSelect, Textarea } from '@doccareplus/ui';
import { BLOOD_GROUPS } from '../../domain/patientView';
import type { RegisterPatientFields } from './useRegisterPatientForm';
import type { PatientDuplicateMatchResponse } from '../../infrastructure/patients.api';

const GENDERS = ['Male', 'Female', 'Other'] as const;

interface Props {
  fields: RegisterPatientFields;
  setField: (name: keyof RegisterPatientFields, value: string) => void;
  error: string | null;
  onSubmit: () => void;
  duplicateMatches: PatientDuplicateMatchResponse[];
  duplicateReason: string;
  setDuplicateReason: (value: string) => void;
}

/**
 * PatientRegisterFields — the register-patient form body, shared by the desktop
 * modal and mobile sheet. Presentational: renders design-system fields and
 * forwards changes. Name and phone are required; everything else can be added later.
 */
export function PatientRegisterFields({ fields, setField, error, onSubmit, duplicateMatches, duplicateReason, setDuplicateReason }: Props) {
  const { t } = useTranslation('patients');
  // A same-name-and-phone hit is the high-confidence "same patient" case — warn more strongly.
  const hasStrongMatch = duplicateMatches.some((match) => match.matchReasons.includes('NameAndPhone'));

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      {error && (
        <div className="rounded-[10px] border border-[var(--danger)] bg-[var(--danger-bg)] p-2.5 text-[12.5px] font-medium text-[var(--danger-strong)]">
          {error}
        </div>
      )}

      {duplicateMatches.length > 0 && (
        <section className={`space-y-3 rounded-xl border p-3 ${hasStrongMatch ? 'border-[var(--danger)] bg-[var(--danger-bg)]' : 'border-[var(--warning)] bg-[var(--warning-bg)]'}`} aria-label={t('register.duplicates.title')}>
          <div><h3 className={`font-bold ${hasStrongMatch ? 'text-[var(--danger-strong)]' : 'text-[var(--warning-strong)]'}`}>{t(hasStrongMatch ? 'register.duplicates.strongTitle' : 'register.duplicates.title')}</h3><p className="text-xs text-text-2">{t(hasStrongMatch ? 'register.duplicates.strongDescription' : 'register.duplicates.description')}</p></div>
          {duplicateMatches.map((match) => (
            <div key={match.id} className="rounded-lg border border-border bg-surface p-3">
              <div className="flex items-start justify-between gap-3"><div><div className="font-semibold text-text">{match.fullName}</div><div className="text-xs text-text-2">{match.patientNumber} · {match.phone ?? t('chart.notProvided')}</div></div><span className="text-xs font-semibold text-warning">{match.matchReasons.join(', ')}</span></div>
              <a href={`/patients/${match.id}`} className="mt-2 inline-flex text-xs font-bold text-primary hover:underline">{t('register.duplicates.openChart')}</a>
            </div>
          ))}
          <Textarea label={t('register.duplicates.reasonLabel')} requiredMark rows={2} value={duplicateReason} onChange={(event) => setDuplicateReason(event.target.value)} placeholder={t('register.duplicates.reasonPlaceholder')} />
        </section>
      )}

      <Input
        label={t('register.nameLabel')}
        requiredMark
        autoFocus
        placeholder={t('register.namePlaceholder')}
        value={fields.fullName}
        onChange={(event) => setField('fullName', event.target.value)}
      />

      <PhoneInput
        label={t('register.phoneLabel')}
        requiredMark
        country={fields.phoneCountry}
        onCountryChange={(iso2) => setField('phoneCountry', iso2)}
        value={fields.phone}
        onChange={(national) => setField('phone', national)}
        placeholder={t('register.phonePlaceholder')}
        countryButtonLabel={t('register.countryButtonLabel')}
        searchPlaceholder={t('common:actions.search')}
        noResultsLabel={t('common:list.noResults', { defaultValue: 'No results' })}
        maxLength={nationalPhoneLengthLimit(fields.phoneCountry)}
      />

      <div className="grid grid-cols-2 gap-3">
        <DateField
          label={t('register.dobLabel')}
          optional
          value={fields.dateOfBirth || null}
          max={toISODateString(new Date())}
          onChange={(value) => setField('dateOfBirth', value)}
          placeholder={t('common:datePicker.chooseDate')}
          prevMonthLabel={t('common:datePicker.previousMonth')}
          nextMonthLabel={t('common:datePicker.nextMonth')}
        />
        <SearchSelect
          label={t('register.genderLabel')}
          optional
          searchable={false}
          value={fields.gender}
          onChange={(value) => setField('gender', value)}
          placeholder={t('register.genderUnspecified')}
          options={[
            { value: '', label: t('register.genderUnspecified') },
            ...GENDERS.map((gender) => ({ value: gender, label: t(`gender.${gender.toLowerCase()}`) })),
          ]}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <SearchSelect
          label={t('register.bloodGroupLabel')}
          optional
          value={fields.bloodGroup}
          onChange={(value) => setField('bloodGroup', value)}
          placeholder={t('register.bloodGroupUnknown')}
          searchPlaceholder={t('common:actions.search')}
          noResultsLabel={t('common:list.noResults', { defaultValue: 'No results' })}
          options={[
            { value: '', label: t('register.bloodGroupUnknown') },
            ...BLOOD_GROUPS.map((group) => ({ value: group.value, label: group.label })),
          ]}
        />
        <Input
          label={t('register.emailLabel')}
          optional
          type="email"
          placeholder={t('register.emailPlaceholder')}
          value={fields.email}
          onChange={(event) => setField('email', event.target.value)}
        />
      </div>

      <Input
        label={t('register.allergyLabel')}
        optional
        placeholder={t('register.allergyPlaceholder')}
        value={fields.allergySummary}
        onChange={(event) => setField('allergySummary', event.target.value)}
      />
      {fields.allergySummary && <p className="text-xs text-warning">{t('register.allergyStructuredPrompt')}</p>}

      <Textarea
        label={t('register.addressLabel')}
        optional
        rows={2}
        value={fields.address}
        onChange={(event) => setField('address', event.target.value)}
      />

      <Textarea
        label={t('register.notesLabel')}
        optional
        rows={2}
        placeholder={t('register.notesPlaceholder')}
        value={fields.notes}
        onChange={(event) => setField('notes', event.target.value)}
      />

      <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
    </form>
  );
}
